import { useCallback, useEffect, useRef, useState } from 'react';
import {
  getRoadmapConfigs,
  saveRoadmapConfig,
  deleteRoadmapConfig,
} from '../utils/roadmapConfigsApi';

const STORAGE_KEY = 'mabel_roadmap_configs';

const DEFAULT_PHASE_PALETTE = [
  '#5C6B8A', '#4A7B7D', '#5A8B6F', '#8B8A5A',
  '#A07A5A', '#8A6570', '#6B5C8A', '#7D4A7B',
];

const STORYBOND_DEFAULTS = {
  title: 'M3: Pre-Launch',
  subtitle: 'Storybond Roadmap',
  phases: [
    { id: 1, name: 'Stability & foundation', color: '#5C6B8A' },
    { id: 2, name: 'NPC quality', color: '#4A7B7D' },
    { id: 3, name: 'Interaction & items', color: '#5A8B6F' },
    { id: 4, name: 'First impressions & data', color: '#8B8A5A' },
    { id: 5, name: 'Safety & moderation', color: '#A07A5A' },
    { id: 6, name: 'Final pass', color: '#8A6570' },
  ],
};

function blankConfigForProject(project) {
  return {
    title: 'Roadmap',
    subtitle: `${project} Roadmap`,
    phases: [
      { id: 1, name: 'Phase 1', color: DEFAULT_PHASE_PALETTE[0] },
    ],
  };
}

function loadCachedConfigs() {
  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved) return JSON.parse(saved);
  } catch (err) {
    console.warn('Failed to load cached roadmap configs:', err);
  }
  return null;
}

function writeCache(configs) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(configs));
  } catch (err) {
    console.warn('Failed to cache roadmap configs:', err);
  }
}

/**
 * useRoadmapConfig - Per-project roadmap config (title, subtitle, phases).
 *
 * Source of truth is the server (`/api/roadmap-configs`). localStorage is
 * a startup cache so the UI renders something before the network round-trip.
 *
 * On first authenticated load, any configs that exist only in localStorage
 * (from before server-side storage existed) are pushed up and become the
 * initial server state.
 */
export function useRoadmapConfig(getToken, isAuthenticated = false) {
  const [configs, setConfigs] = useState(() => {
    const cached = loadCachedConfigs();
    return cached || { Storybond: STORYBOND_DEFAULTS };
  });
  const [syncError, setSyncError] = useState(null);

  // Pending per-project saves; debounce so rapid edits coalesce.
  const saveTimersRef = useRef({});
  const initialLoadDoneRef = useRef(false);

  const safeGetToken = useCallback(async () => {
    if (!isAuthenticated || typeof getToken !== 'function') return null;
    try {
      return await getToken();
    } catch (_) {
      return null;
    }
  }, [getToken, isAuthenticated]);

  // Initial fetch + one-time migration of any localStorage-only configs.
  useEffect(() => {
    if (!isAuthenticated) return;
    if (initialLoadDoneRef.current) return;

    let cancelled = false;
    (async () => {
      const token = await safeGetToken();
      if (!token || cancelled) return;

      try {
        const result = await getRoadmapConfigs(token);
        if (cancelled) return;
        const serverConfigs = result?.configs || {};

        // Migrate cached-only entries to the server (one-shot).
        const cached = loadCachedConfigs() || {};
        const migrations = [];
        for (const [project, cfg] of Object.entries(cached)) {
          if (!serverConfigs[project] && cfg && Array.isArray(cfg.phases)) {
            migrations.push(
              saveRoadmapConfig(token, project, cfg).then(r => {
                if (r?.success && r.config) serverConfigs[project] = r.config;
              }).catch(err => {
                console.warn(`Failed to migrate roadmap config for ${project}:`, err);
              })
            );
          }
        }
        if (migrations.length > 0) await Promise.all(migrations);

        if (cancelled) return;
        // If the server still has nothing, seed Storybond defaults so the
        // existing UX continues to work for first-time users.
        if (Object.keys(serverConfigs).length === 0) {
          try {
            const seeded = await saveRoadmapConfig(token, 'Storybond', STORYBOND_DEFAULTS);
            if (seeded?.success && seeded.config) {
              serverConfigs.Storybond = seeded.config;
            } else {
              serverConfigs.Storybond = STORYBOND_DEFAULTS;
            }
          } catch (err) {
            console.warn('Failed to seed default Storybond config:', err);
            serverConfigs.Storybond = STORYBOND_DEFAULTS;
          }
        }

        if (cancelled) return;
        setConfigs(serverConfigs);
        writeCache(serverConfigs);
        initialLoadDoneRef.current = true;
      } catch (err) {
        console.warn('Failed to load roadmap configs:', err);
      }
    })();

    return () => { cancelled = true; };
  }, [isAuthenticated, safeGetToken]);

  // Always keep localStorage in sync as a cache
  useEffect(() => {
    writeCache(configs);
  }, [configs]);

  const scheduleSave = useCallback((project, config) => {
    if (!isAuthenticated) return;
    // Block saves until the initial server fetch has completed. Otherwise
    // an edit made during the brief load window could push stale localStorage
    // cache up and overwrite newer server data.
    if (!initialLoadDoneRef.current) return;
    if (saveTimersRef.current[project]) {
      clearTimeout(saveTimersRef.current[project]);
    }
    saveTimersRef.current[project] = setTimeout(async () => {
      const token = await safeGetToken();
      if (!token) return;
      try {
        await saveRoadmapConfig(token, project, config);
        setSyncError(null);
      } catch (err) {
        console.warn(`Failed to save roadmap config for ${project}:`, err);
        setSyncError(err?.message || 'Failed to save roadmap config');
      }
    }, 600);
  }, [isAuthenticated, safeGetToken]);

  const updateProject = useCallback((project, mutator) => {
    setConfigs(prev => {
      const current = prev[project] || blankConfigForProject(project);
      const next = mutator(current);
      if (!next || next === current) return prev;
      const updated = { ...prev, [project]: next };
      scheduleSave(project, next);
      return updated;
    });
  }, [scheduleSave]);

  const getConfig = useCallback((project) => {
    if (!project) return null;
    return configs[project] || blankConfigForProject(project);
  }, [configs]);

  const ensureConfig = useCallback((project) => {
    if (!project) return;
    setConfigs(prev => {
      if (prev[project]) return prev;
      const fresh = blankConfigForProject(project);
      scheduleSave(project, fresh);
      return { ...prev, [project]: fresh };
    });
  }, [scheduleSave]);

  const setHeader = useCallback((project, { title, subtitle }) => {
    updateProject(project, current => ({
      ...current,
      ...(title !== undefined ? { title } : {}),
      ...(subtitle !== undefined ? { subtitle } : {}),
    }));
  }, [updateProject]);

  const renamePhase = useCallback((project, phaseId, name) => {
    updateProject(project, current => ({
      ...current,
      phases: current.phases.map(p => p.id === phaseId ? { ...p, name } : p),
    }));
  }, [updateProject]);

  const addPhase = useCallback((project) => {
    updateProject(project, current => {
      const nextId = current.phases.reduce((m, p) => Math.max(m, p.id), 0) + 1;
      const color = DEFAULT_PHASE_PALETTE[(nextId - 1) % DEFAULT_PHASE_PALETTE.length];
      return {
        ...current,
        phases: [...current.phases, { id: nextId, name: `Phase ${nextId}`, color }],
      };
    });
  }, [updateProject]);

  const removePhase = useCallback((project, phaseId) => {
    updateProject(project, current => {
      if (current.phases.length <= 1) return current;
      return {
        ...current,
        phases: current.phases.filter(p => p.id !== phaseId),
      };
    });
  }, [updateProject]);

  const movePhase = useCallback((project, phaseId, direction) => {
    updateProject(project, current => {
      const idx = current.phases.findIndex(p => p.id === phaseId);
      if (idx === -1) return current;
      const newIdx = direction === 'up' ? idx - 1 : idx + 1;
      if (newIdx < 0 || newIdx >= current.phases.length) return current;
      const phases = [...current.phases];
      [phases[idx], phases[newIdx]] = [phases[newIdx], phases[idx]];
      return { ...current, phases };
    });
  }, [updateProject]);

  const removeProjectConfig = useCallback(async (project) => {
    setConfigs(prev => {
      if (!prev[project]) return prev;
      const next = { ...prev };
      delete next[project];
      return next;
    });
    if (saveTimersRef.current[project]) {
      clearTimeout(saveTimersRef.current[project]);
      delete saveTimersRef.current[project];
    }
    const token = await safeGetToken();
    if (!token) return;
    try {
      await deleteRoadmapConfig(token, project);
    } catch (err) {
      console.warn(`Failed to delete roadmap config for ${project}:`, err);
    }
  }, [safeGetToken]);

  return {
    configs,
    getConfig,
    ensureConfig,
    setHeader,
    renamePhase,
    addPhase,
    removePhase,
    movePhase,
    removeProjectConfig,
    syncError,
  };
}

export default useRoadmapConfig;
